import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, Globe, Zap, Users, Leaf, Building2 } from 'lucide-react';

const VisionSection = () => {
  const visionPillars = [
    {
      title: "Économie productive et compétitive",
      description: "Transformation locale, industrialisation et maîtrise des chaînes de valeur pour une économie souveraine",
      icon: <Building2 className="w-8 h-8" />,
      color: "text-primary",
      bgColor: "bg-primary/10",
      projects: [
        "Développement de l'industrie locale",
        "Valorisation des ressources naturelles",
        "Création de chaînes de valeur intégrées",
        "Promotion de l'entrepreneuriat"
      ]
    },
    {
      title: "Société inclusive et équitable",
      description: "Accès équitable aux services essentiels, éducation, santé et emploi pour tous les citoyens",
      icon: <Users className="w-8 h-8" />,
      color: "text-secondary",
      bgColor: "bg-secondary/10",
      projects: [
        "Amélioration du système éducatif",
        "Renforcement du système de santé",
        "Création d'emplois pour les jeunes",
        "Promotion de l'égalité des genres"
      ]
    },
    {
      title: "Transition écologique",
      description: "Développement durable et protection de l'environnement pour les générations futures",
      icon: <Leaf className="w-8 h-8" />,
      color: "text-accent",
      bgColor: "bg-accent/10",
      projects: [
        "Énergies renouvelables",
        "Gestion durable des ressources",
        "Protection de la biodiversité",
        "Lutte contre le changement climatique"
      ]
    },
    {
      title: "Innovation et technologie",
      description: "New Deal Technologique pour faire du Sénégal un leader de l'économie numérique en Afrique",
      icon: <Zap className="w-8 h-8" />,
      color: "text-primary",
      bgColor: "bg-primary/10",
      projects: [
        "Digitalisation de l'administration",
        "Développement des startups tech",
        "Formation aux métiers du numérique",
        "Infrastructure numérique moderne"
      ]
    }
  ];

  const strategicAxes = [
    {
      title: "Souveraineté économique",
      description: "Réduction de la dépendance extérieure et valorisation des ressources nationales"
    },
    {
      title: "Justice sociale",
      description: "Réduction des inégalités et promotion de l'équité pour tous"
    },
    {
      title: "Intégration africaine",
      description: "Renforcement de la coopération et de l'unité continentale"
    },
    {
      title: "Gouvernance transparente",
      description: "Institutions fortes, transparentes et au service du peuple"
    }
  ];

  return (
    <section id="vision" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Vision et Projets
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Découvrez la vision transformatrice du Président Bassirou Diomaye Faye 
              pour un Sénégal souverain, juste et prospère à l'horizon 2050.
            </p>
            <Badge variant="secondary" className="text-lg px-6 py-2 font-semibold">
              Sénégal Vision 2050
            </Badge>
          </div>

          {/* Vision principale */}
          <Card className="mb-16 shadow-elegant bg-gradient-primary text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <Globe className="w-16 h-16 mx-auto mb-6 text-secondary" />
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                "Une nation souveraine, juste et prospère"
              </h3>
              <p className="text-xl md:text-2xl font-light leading-relaxed max-w-4xl mx-auto">
                La Vision Sénégal 2050 incarne l'ambition stratégique de bâtir une nation résiliente, 
                inclusive et pleinement souveraine, en harmonie avec ses valeurs, ses ressources 
                et les aspirations de son peuple.
              </p>
            </CardContent>
          </Card>

          {/* Piliers de la vision */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-center mb-12 text-primary">
              Les quatre piliers fondamentaux
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {visionPillars.map((pillar, index) => (
                <Card key={index} className="shadow-card hover:shadow-elegant transition-all duration-300 transform hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`p-4 rounded-lg ${pillar.bgColor}`}>
                        <div className={pillar.color}>
                          {pillar.icon}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-primary">
                          {pillar.title}
                        </h4>
                      </div>
                    </div>
                    
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {pillar.description}
                    </p>
                    
                    <div className="space-y-2">
                      <h5 className="font-semibold text-sm text-primary mb-3">Projets clés :</h5>
                      {pillar.projects.map((project, projectIndex) => (
                        <div key={projectIndex} className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-secondary flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{project}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Axes stratégiques */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-center mb-12 text-primary">
              Axes stratégiques de transformation
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {strategicAxes.map((axis, index) => (
                <Card key={index} className="shadow-card hover:shadow-elegant transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-primary mb-2">
                          {axis.title}
                        </h4>
                        <p className="text-muted-foreground text-sm">
                          {axis.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Citation de clôture */}
          <Card className="shadow-elegant border-l-4 border-l-primary">
            <CardContent className="p-8">
              <blockquote className="text-xl md:text-2xl font-light italic text-center text-muted-foreground mb-4">
                "Je lance un appel à nos frères et sœurs africains pour qu'ensemble nous consolidions 
                les acquis obtenus dans les processus de construction de l'intégration, 
                tout en corrigeant les faiblesses et en changeant certaines méthodes."
              </blockquote>
              <cite className="block text-center text-lg font-semibold text-primary">
                — Président Bassirou Diomaye Faye
              </cite>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;